import React, { useState } from 'react'
import { Theme, ColorScheme, FontWeight } from '../types'
import { FontPicker } from './FontPicker'
import { StylePicker } from './StylePicker'
import { ColorEditor, ColorKey } from './ColorEditor'

type Tab = 'design' | 'colors'

interface ThemeEditorProps {
  theme: Theme
  prefersDarkMode: boolean
  onUpdate: (updates: Partial<Theme>) => void
}

export function ThemeEditor({ theme, prefersDarkMode, onUpdate }: ThemeEditorProps) {
  const [activeTab, setActiveTab] = useState<Tab>('design')

  const handleColorChange = (mode: 'lightMode' | 'darkMode', key: ColorKey, value: string) => {
    const colors = mode === 'darkMode' ? theme.darkMode : theme.lightMode
    onUpdate({
      [mode]: {
        ...colors,
        [key]: value,
      },
    })
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'design',
      label: 'Design',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
        </svg>
      ),
    },
    {
      key: 'colors',
      label: 'Colors',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab Bar */}
      <div className="flex border-b border-gray-200 bg-gray-50/50 px-3 pt-2 gap-1 flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-md border border-b-0 transition-colors cursor-pointer -mb-px ${
              activeTab === tab.key
                ? 'bg-white text-gray-900 border-gray-200'
                : 'bg-transparent text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'design' ? (
          <DesignTab theme={theme} onUpdate={onUpdate} />
        ) : (
          <ColorEditor theme={theme} onColorChange={handleColorChange} />
        )}
      </div>
    </div>
  )
}

/* ============================================================
   Design Tab — Name, Typography, Style
   ============================================================ */

function DesignTab({ theme, onUpdate }: { theme: Theme; onUpdate: (updates: Partial<Theme>) => void }) {
  return (
    <div className="p-4 space-y-5">
      {/* Theme Identity */}
      <Section title="Identity">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Theme Name</label>
            <input
              type="text"
              value={theme.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-gray-400 focus:outline-none"
              placeholder="Theme name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea
              value={theme.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md resize-none focus:border-gray-400 focus:outline-none"
              placeholder="Theme description..."
            />
          </div>
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography">
        <div className="space-y-4">
          <FontPicker
            label="Display Font"
            value={theme.displayFont}
            weight={theme.displayWeight}
            onFontChange={(font) => onUpdate({ displayFont: font })}
            onWeightChange={(weight) => onUpdate({ displayWeight: weight })}
            type="display"
          />
          <FontPicker
            label="Body Font"
            value={theme.bodyFont}
            weight={theme.bodyWeight}
            onFontChange={(font) => onUpdate({ bodyFont: font })}
            onWeightChange={(weight) => onUpdate({ bodyWeight: weight })}
            type="body"
          />
        </div>
      </Section>

      {/* Style */}
      <Section title="Style">
        <StylePicker
          borderRadius={theme.borderRadius}
          shadow={theme.shadow}
          onBorderRadiusChange={(value) => onUpdate({ borderRadius: value })}
          onShadowChange={(value) => onUpdate({ shadow: value })}
        />
      </Section>
    </div>
  )
}

/* ============================================================
   Collapsible Section wrapper
   ============================================================ */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true)

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left cursor-pointer group"
      >
        <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider">{title}</h3>
        <svg
          className={`w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-transform ${
            open ? '' : '-rotate-90'
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  )
}
